import { API } from "@swivl-mono/api-helper";
import { DEFAULT_GROUP_SCHEDULE_SLUG, LocationVarInheritance, LocationVarKey, RoutingGroupInterface, ScheduleSlug } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonVariant, InheritancePill, Panel, PanelPosition, PhoneInput, Select, SelectSize } from "@swivl-mono/ui";
import parsePhoneNumberFromString from "libphonenumber-js";
import { useEffect, useMemo, useState } from "react";
import { BiEditAlt } from "react-icons/bi";
import { IoInformationCircleOutline } from "react-icons/io5";

import { SearchableLabel } from "../../../Components/Worksheets/Types/Worksheet.Interface";
import { usePropertiesGetLocationIds } from "../../../Hooks/usePropertiesGetLocationIds";
import { Member } from "../../../Models/Member/Member.Current";
import { FlyoutField, RecipientListEditor, validateEmail, validatePhone } from "../Components/RecipientListEditor";

const EMERGENCY_SLUG = "emergency";

// Hand-authored — rows carry live handlers, can't be data-driven.
export const SEARCH_LABELS: SearchableLabel[] = [
  { title: "Emergency", snippet: "Where notifications go during an emergency." },
  { title: "Forward number (during business hours)" },
  { title: "Forward number (outside business hours)" },
  { title: "Hours", snippet: "Which schedule decides open vs. closed." },
  { title: "SMS alert recipients" },
  { title: "Email alert recipients" }
];

// The schedule slugs an operator can point the emergency group at, mirroring
// the choices the Hours page surfaces. Defaults to the call-center schedule —
// the same fallback the bot uses (see `Hours.Helper`): when the group has no
// explicit `scheduleSlug`, hours resolve against `call-center`.
const SCHEDULE_SLUG_OPTIONS: ReadonlyArray<{ slug: ScheduleSlug; label: string }> = [
  { slug: "call-center", label: "Call Center Hours" },
  { slug: "office-hours", label: "Office Hours" },
  { slug: "gate-access", label: "Gate Access" }
];

/**
 * Format an E.164 number for read-only display as "+1 728-283-0023" — a space
 * after the country code, dashes between the national groups. Falls back to the
 * raw value when it can't be parsed.
 */
function formatPhoneForDisplay(value: string | null | undefined): string {
  if (!value) return "";
  const parsed = parsePhoneNumberFromString(value);
  if (!parsed) return value;
  const [callingCode, ...nationalGroups] = parsed.formatInternational().split(" ");
  return nationalGroups.length > 0 ? `${callingCode} ${nationalGroups.join("-")}` : parsed.formatInternational();
}

/**
 * Narrow a stored `scheduleSlug` (a free string on the row) to one of the
 * known emergency schedule options, defaulting to the call-center schedule —
 * the same fallback the bot uses.
 */
function resolveScheduleSlug(stored: string | null | undefined): ScheduleSlug {
  const known = SCHEDULE_SLUG_OPTIONS.find((o) => o.slug === stored);
  return known ? known.slug : DEFAULT_GROUP_SCHEDULE_SLUG;
}

/**
 * PM v3 Emergency tab.
 *
 * Edits the single emergency-slug `RoutingGroupEntity` row at the active
 * scope. The page is a read-only "Configuration" card (mirroring the
 * Escalation & Ticketing cards) with an "Edit Emergency" flyout that
 * surfaces the safety-critical fields (in-hours forward, after-hours
 * forward, hours/schedule, alert SMS recipients, alert email recipients),
 * blocked-save validation on the required in-hours number, and the same
 * customize-down pattern as the generic Escalation & Ticketing page.
 *
 * The schedule's weekly hours are edited on the dedicated Hours page — this
 * tab only chooses which schedule applies (mirroring the Escalation &
 * Ticketing flyout).
 *
 * Topics, role lists, ticket-action / escalation-mode / outside-hours-mode
 * are intentionally NOT exposed — emergency is locked to
 * `defaultTicketAction: "ticket-and-human"` + `escalationMode:
 * "call-forward"` + `outsideHoursMode: "forward"`. The `report_emergency`
 * bot tool implies all three.
 *
 * See `docs/snowy/plans/2026-06-05-emergency-consolidation.md`.
 */

// PAGE_KEYS exported for the nav data — empty array because this page
// reads via the RoutingGroup API, not via the LocationVar topic system.
export const PAGE_KEYS: LocationVarKey[] = [];

export default function EmergencyPage(): React.ReactElement {
  const orgId = Member.orgId!;
  const page = usePropertiesGetLocationIds();
  const activeGroupId: number | null = page.groupId ?? null;
  const activeLocationId: number | null = page.locationId ?? null;

  const [allGroups, setAllGroups] = useState<RoutingGroupInterface[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const groups = await API.RoutingGroup.listGroups(orgId);
      setAllGroups(groups);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, activeGroupId, activeLocationId]);

  // Find the narrowest emergency-slug row that applies at the active scope.
  // Cascade: location > group > org. Mirrors the resolver.
  const { resolved, inheritance } = useMemo<{ resolved: RoutingGroupInterface | null; inheritance: LocationVarInheritance }>(() => {
    const matches = allGroups.filter((g) => g.slug === EMERGENCY_SLUG && !g.isArchived);
    if (activeLocationId != null) {
      const loc = matches.find((g) => g.locationId === activeLocationId);
      if (loc) return { resolved: loc, inheritance: LocationVarInheritance.NONE };
    }
    if (activeGroupId != null) {
      const grp = matches.find((g) => g.locationId == null && g.groupId === activeGroupId);
      if (grp) return { resolved: grp, inheritance: activeLocationId != null ? LocationVarInheritance.GROUP : LocationVarInheritance.NONE };
    }
    const org = matches.find((g) => g.groupId == null && g.locationId == null);
    if (org)
      return {
        resolved: org,
        inheritance: activeGroupId == null && activeLocationId == null ? LocationVarInheritance.NONE : LocationVarInheritance.ORG
      };
    return { resolved: null, inheritance: LocationVarInheritance.NONE };
  }, [allGroups, activeGroupId, activeLocationId]);

  const isCustom = inheritance === LocationVarInheritance.NONE;

  // "Revert to Default" is meaningful only when the row is custom AT a
  // sub-scope AND a broader-scope emergency row exists to fall back to. At
  // org scope there's nothing broader.
  const canRevert = useMemo(() => {
    if (!isCustom) return false;
    if (activeLocationId == null && activeGroupId == null) return false; // at org scope, nothing to revert to
    return allGroups.some(
      (g) =>
        g.slug === EMERGENCY_SLUG &&
        !g.isArchived &&
        g.id !== resolved?.id &&
        ((g.locationId == null && g.groupId == null) || (activeLocationId != null && g.groupId == null))
    );
  }, [allGroups, isCustom, resolved?.id, activeGroupId, activeLocationId]);

  // Buffered edit state — seeded from the resolved row, re-synced when the
  // resolved id changes (after save / revert / scope switch). The read-only
  // card always shows the saved (`resolved`) values; the flyout edits these.
  const [escalationForwardPhoneNumber, setEscalationForwardPhoneNumber] = useState("");
  const [outsideHoursForwardPhoneNumber, setOutsideHoursForwardPhoneNumber] = useState("");
  const [alertSmsRecipients, setAlertSmsRecipients] = useState<string[]>([]);
  const [alertEmailRecipients, setAlertEmailRecipients] = useState<string[]>([]);
  const [scheduleSlug, setScheduleSlug] = useState<ScheduleSlug>(DEFAULT_GROUP_SCHEDULE_SLUG);
  const [forwardError, setForwardError] = useState<string | null>(null);
  const [outsideHoursError, setOutsideHoursError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEscalationForwardPhoneNumber(resolved?.escalationForwardPhoneNumber ?? "");
    setOutsideHoursForwardPhoneNumber(resolved?.outsideHoursForwardPhoneNumber ?? "");
    setAlertSmsRecipients(resolved?.alertSmsRecipients ?? []);
    setAlertEmailRecipients(resolved?.alertEmailRecipients ?? []);
    setScheduleSlug(resolveScheduleSlug(resolved?.scheduleSlug));
  }, [resolved?.id]);

  const inHoursTrimmed = escalationForwardPhoneNumber.trim();
  // Any non-empty SMS recipient must be a valid phone number before saving.
  const hasInvalidSms = alertSmsRecipients.some((r) => r.trim() !== "" && validatePhone(r) !== null);
  const canSave = inHoursTrimmed.length > 0 && !forwardError && !outsideHoursError && !hasInvalidSms;

  // Reseed the buffered fields from the saved row, then open the flyout —
  // so a previous unsaved edit that was cancelled doesn't linger.
  const openEditor = () => {
    setEscalationForwardPhoneNumber(resolved?.escalationForwardPhoneNumber ?? "");
    setOutsideHoursForwardPhoneNumber(resolved?.outsideHoursForwardPhoneNumber ?? "");
    setAlertSmsRecipients(resolved?.alertSmsRecipients ?? []);
    setAlertEmailRecipients(resolved?.alertEmailRecipients ?? []);
    setScheduleSlug(resolveScheduleSlug(resolved?.scheduleSlug));
    setForwardError(null);
    setOutsideHoursError(null);
    setIsEditing(true);
  };

  const revert = async () => {
    if (!canRevert || !resolved) return;
    if (!window.confirm("Delete this scope's emergency override and fall back to the inherited settings?")) return;
    setSaving(true);
    try {
      await API.RoutingGroup.deleteGroup(orgId, resolved.id);
      await reload();
      setIsEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  // Save upserts at the active scope with the locked emergency invariants.
  // When the active scope was inheriting, this creates the override (the
  // "Customize" action); when no emergency row exists anywhere yet, it
  // creates a fresh one (so the page works even on an un-seeded org);
  // otherwise it updates the existing custom row.
  const save = async () => {
    // Re-validate on save in case the user never blurred the fields.
    const fErr = validatePhone(escalationForwardPhoneNumber);
    const oErr = validatePhone(outsideHoursForwardPhoneNumber);
    setForwardError(fErr);
    setOutsideHoursError(oErr);
    if (!inHoursTrimmed || fErr || oErr) return;
    setSaving(true);
    try {
      await API.RoutingGroup.upsertGroup(orgId, {
        slug: EMERGENCY_SLUG,
        topics: [EMERGENCY_SLUG],
        // Identity / non-exposed fields: preserve the resolved row's values,
        // or fall back to the seed defaults when creating from scratch.
        name: resolved?.name ?? "Emergency",
        description: resolved?.description ?? "Life-safety escalations: trapped at gate, fire, medical, in-progress break-in.",
        isEnabled: resolved?.isEnabled ?? true,
        roleSlugs: resolved?.roleSlugs ?? ["admin", "agent", "member"],
        ticketAssignment: resolved?.ticketAssignment ?? "assign-to-one",
        ticketAssignmentPick: resolved?.ticketAssignmentPick ?? "least-busy",
        // Locked invariants for the emergency row — see comment at the top of the file.
        defaultTicketAction: "ticket-and-human",
        escalationMode: "call-forward",
        outsideHoursMode: "forward",
        escalationCallCenterDispatch: null,
        escalationCallCenterPick: null,
        escalationForwardPhoneNumber: inHoursTrimmed,
        outsideHoursForwardPhoneNumber: outsideHoursForwardPhoneNumber.trim() || null,
        alertSmsRecipients: alertSmsRecipients.map((r) => r.trim()).filter(Boolean),
        alertEmailRecipients: alertEmailRecipients.map((r) => r.trim()).filter(Boolean),
        scheduleSlug,
        groupId: activeLocationId != null ? null : activeGroupId,
        locationId: activeLocationId
      });
      await reload();
      setIsEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <EmergencyPageSkeleton />;
  }

  const smsValue = (resolved?.alertSmsRecipients ?? []).join(", ") || "None";
  const emailValue = (resolved?.alertEmailRecipients ?? []).join(", ") || "None";
  const resolvedScheduleLabel =
    SCHEDULE_SLUG_OPTIONS.find((o) => o.slug === resolveScheduleSlug(resolved?.scheduleSlug))?.label ?? "Call Center Hours";

  return (
    <>
      {/* Same container as CortexTopicWorksheet, so the cards line up with the
          other Details v3 pages. Keep the max-width in sync with it. */}
      <div className="mx-auto max-w-[960px] space-y-4 p-6">
        {/* Yellow warning banner */}
        <div className="rounded border border-yellow-700 bg-yellow-100 p-3">
          <div className="flex items-center gap-2">
            <IoInformationCircleOutline className="size-4 shrink-0 text-yellow-900" />
            <span className="text-sm font-semibold text-yellow-900">Emergency</span>
          </div>
          <p className="pl-6 text-sm text-yellow-900">
            These settings control where notifications go when an emergency is detected. Misconfiguring these settings could delay notifications for
            time-sensitive situations. Please verify after every change.
          </p>
        </div>

        {error && <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

        {/* Read-only Configuration card */}
        <section className="flex flex-col gap-4 rounded-lg border border-grey-200 bg-white p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-grey-900">Configuration</h3>
              <InheritancePill
                inheritance={inheritance}
                isEditing={false}
                canRevert={false}
                alwaysShow
                customText={{ custom: "Customized" }}
                onClick={() => {}}
              />
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <button
                type="button"
                onClick={openEditor}
                className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-grey-900 hover:text-orange-500"
              >
                Edit
                <BiEditAlt className="size-4" />
              </button>
            </div>
          </div>

          <div className="border-t border-grey-300" />

          <div className="flex flex-col gap-2">
            <SettingRow label="Forward number (during business hours)" value={formatPhoneForDisplay(resolved?.escalationForwardPhoneNumber) || "—"} />
            <SettingRow
              label="Forward number (outside business hours)"
              value={formatPhoneForDisplay(resolved?.outsideHoursForwardPhoneNumber) || "—"}
            />
            <SettingRow label="Hours" value={resolvedScheduleLabel} />
            <SettingRow label="SMS alert recipients" value={smsValue} />
            <SettingRow label="Email alert recipients" value={emailValue} />
          </div>
        </section>
      </div>

      {/* Edit Emergency flyout */}
      <Panel
        isOpen={isEditing}
        onToggle={() => setIsEditing(false)}
        position={PanelPosition.RIGHT}
        width="480px"
        title="Edit Emergency"
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        // PhoneInput's country dropdown and the Select / Tooltip portals render
        // outside the panel DOM; without this, interacting with them reads as
        // an outside click and closes the flyout.
        shouldIgnoreOutsideClick={(e) =>
          e.target instanceof Element && !!e.target.closest("[data-radix-popper-content-wrapper],[data-floating-ui-portal]")
        }
      >
        {isEditing && (
          <div className="flex h-full flex-col bg-white">
            <div className="flex h-[50px] shrink-0 items-center gap-2 border-b border-grey-200 px-4">
              <p className="text-base font-semibold text-grey-900">Edit Emergency</p>
              <InheritancePill
                inheritance={inheritance}
                isEditing={false}
                canRevert={false}
                alwaysShow
                customText={{ custom: "Customized" }}
                onClick={() => {}}
              />
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              <div className="flex flex-col gap-4">
                <FlyoutField
                  label="Forward number (during business hours)"
                  required
                  tooltip="Required. The number calls forward to when the bot fires report_emergency. Hours-gate is bypassed — emergencies forward 24/7. The bot never reads this number aloud (often a personal cell)."
                >
                  <PhoneInput
                    defaultValue={resolved?.escalationForwardPhoneNumber ?? ""}
                    onChange={(v) => {
                      setEscalationForwardPhoneNumber(v);
                      setForwardError(null);
                    }}
                    onBlur={() => setForwardError(validatePhone(escalationForwardPhoneNumber))}
                    error={forwardError ?? undefined}
                  />
                </FlyoutField>

                <FlyoutField
                  label="Forward number (outside business hours)"
                  tooltip="Optional. Used outside the team's hours if you want a different destination. Leave blank to use the in-hours number for both windows."
                >
                  <PhoneInput
                    defaultValue={resolved?.outsideHoursForwardPhoneNumber ?? ""}
                    onChange={(v) => {
                      setOutsideHoursForwardPhoneNumber(v);
                      setOutsideHoursError(null);
                    }}
                    onBlur={() => setOutsideHoursError(validatePhone(outsideHoursForwardPhoneNumber))}
                    error={outsideHoursError ?? undefined}
                  />
                </FlyoutField>

                <FlyoutField
                  label="Hours"
                  tooltip="Pick which schedule decides whether the emergency group is open — open uses the in-hours number, closed uses the outside-hours number. Edit the schedule's weekly hours on the Hours page; this only chooses which one applies."
                >
                  <Select
                    size={SelectSize.MEDIUM}
                    hideClear
                    selectedValues={[scheduleSlug]}
                    options={SCHEDULE_SLUG_OPTIONS.map((o) => ({ label: o.label, value: o.slug }))}
                    onChange={(vals) => {
                      const next = SCHEDULE_SLUG_OPTIONS.find((o) => o.slug === vals[0]);
                      if (next) setScheduleSlug(next.slug);
                    }}
                  />
                </FlyoutField>

                <RecipientListEditor
                  label="SMS alert recipients"
                  tooltip="Each recipient gets an SMS when the bot fires report_emergency. E.164 phone numbers."
                  entries={alertSmsRecipients}
                  onChange={setAlertSmsRecipients}
                  placeholder="555-123-4567"
                  inputType="phone"
                  validate={validatePhone}
                />

                <RecipientListEditor
                  label="Email alert recipients"
                  tooltip="Each recipient gets an email when the bot fires report_emergency."
                  entries={alertEmailRecipients}
                  onChange={setAlertEmailRecipients}
                  placeholder="ops@example.com"
                  validate={validateEmail}
                />

                {!canSave && (
                  <p className="text-sm text-yellow-900">
                    ⚠️ Set an in-hours forward number before saving. Without it, emergency calls degrade to a queued ticket tagged with{" "}
                    <code className="rounded bg-yellow-50 px-1 font-mono text-xs">routing-group-misconfig</code>.
                  </p>
                )}
              </div>
            </div>

            <div className="flex h-[50px] shrink-0 items-center justify-between gap-2 border-t border-grey-200 bg-white px-4">
              {canRevert ? (
                <Button variant={ButtonVariant.GHOST} className="-ml-3" disabled={saving} onClickAsync={revert}>
                  Revert to Default
                </Button>
              ) : (
                <span />
              )}
              <div className="flex items-center gap-2">
                <Button color={ButtonColor.WHITE} disabled={saving} onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
                <Button color={ButtonColor.ORANGE} disabled={!canSave || saving} onClickAsync={save}>
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}
      </Panel>
    </>
  );
}

function SettingRow(props: { label: string; value: string }): React.ReactElement {
  return (
    <div className="flex gap-6 text-sm">
      <span className="w-[251px] shrink-0 font-semibold text-grey-600">{props.label}</span>
      <span className="min-w-0 break-words text-grey-900">{props.value}</span>
    </div>
  );
}

/**
 * Pulsing placeholder shown while the emergency configuration loads. Mirrors the
 * banner + Configuration-card layout so content doesn't shift in on load.
 */
function EmergencyPageSkeleton(): React.ReactElement {
  return (
    // Same container as the loaded page, so the placeholders sit where the real
    // cards will and nothing jumps when the data arrives.
    <div className="mx-auto max-w-[960px] animate-pulse space-y-4 p-6" aria-hidden>
      <div className="h-16 rounded border border-grey-200 bg-grey-100" />
      <div className="space-y-4 rounded-lg border border-grey-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <div className="h-5 w-32 rounded bg-grey-100" />
          <div className="h-6 w-16 rounded bg-grey-100" />
        </div>
        <div className="border-t border-grey-200" />
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex gap-6">
              <div className="h-4 w-[251px] rounded bg-grey-100" />
              <div className="h-4 flex-1 rounded bg-grey-100" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
